import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Alert,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { UploadFile, ExpandLess, ExpandMore, Description } from "@mui/icons-material";
import type { AppTestTarget, DeletionCandidate, SpecImportSummary } from "../utils/spec-import/types";
import {
  SpecError,
  SpecToTargetConverter,
  type SpecWarning,
} from "../libs/spec-to-target-converter";
import { errorToJapanese, warningToJapanese } from "../utils/spec-import/spec-import-messages";
import { toAppTestTargets } from "../utils/spec-import/to-app-test-target";
import { matchExistingTargets } from "../utils/spec-import/match-existing-targets";
import type { GenerateFromOperationLogResponse } from "../services/operationLogService";

export interface SpecificationImportDialogProps {
  open: boolean;
  onClose: () => void;
  existingTargets: AppTestTarget[];
  onPreviewReady: (
    response: GenerateFromOperationLogResponse,
    summary: SpecImportSummary,
    matchedExistingIds: Set<string>,
    deletionCandidates: DeletionCandidate[],
  ) => void;
}

type DialogState =
  | { kind: "idle" }
  | { kind: "selected"; file: File }
  | { kind: "parsing"; file: File }
  | { kind: "parsed_ok"; file: File; summary: SpecImportSummary; response: GenerateFromOperationLogResponse; matchedExistingIds: Set<string>; deletionCandidates: DeletionCandidate[] }
  | { kind: "parsed_fatal"; file: File; message: string };

// UI 経由インポート時の即時 reject 用。lib 単体使用時の DoS guard
// (`INPUT_LIMITS.maxFileBytes` = 50 MiB) より厳しく、「実運用で 20 MiB 超の仕様書は
// 想定外」という UX 判断で絞る。lib 単体使用 (テスト fixture 等) では 50 MiB まで受ける。
const MAX_FILE_SIZE_MIB = 20;

export const SpecificationImportDialog: React.FC<SpecificationImportDialogProps> = ({
  open,
  onClose,
  existingTargets,
  onPreviewReady,
}) => {
  const [state, setState] = useState<DialogState>({ kind: "idle" });
  const [dragOver, setDragOver] = useState(false);
  // 解析中にダイアログが閉じられたら、完了後の setState を抑止する
  const cancelledRef = useRef(false);

  const handleFile = useCallback((file: File) => {
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setState({ kind: "parsed_fatal", file, message: ".xlsx 形式のファイルを選択してください" });
      return;
    }
    if (file.size > MAX_FILE_SIZE_MIB * 1024 * 1024) {
      setState({
        kind: "parsed_fatal",
        file,
        message: `ファイルサイズが上限 ${MAX_FILE_SIZE_MIB} MiB を超えています`,
      });
      return;
    }
    setState({ kind: "selected", file });
  }, []);

  const runParse = useCallback(async (file: File) => {
    cancelledRef.current = false;
    setState({ kind: "parsing", file });
    try {
      const buffer = await file.arrayBuffer();

      const converter = new SpecToTargetConverter();
      const result = await converter.convert(buffer);
      if (cancelledRef.current) return;
      const rawTargets = toAppTestTargets(result.targets);
      const matched = matchExistingTargets(rawTargets, existingTargets);

      const targetTypeCount = (type: string) =>
        matched.resolvedTargets.filter((t) => t.type === type).length;

      const summary: SpecImportSummary = {
        serviceCount: targetTypeCount("service"),
        viewCount: targetTypeCount("view"),
        componentCount: targetTypeCount("component"),
        itemCount: targetTypeCount("item"),
        sectionCount: targetTypeCount("section"),
        overlayCount: targetTypeCount("overlay"),
        warnings: result.warnings,
        newCount: matched.resolvedTargets.filter(
          (t) => !matched.matchedExistingIds.has(t.test_target_id ?? ""),
        ).length,
        updateCount: matched.matchedExistingIds.size,
        unmatchedExistingCount: matched.unmatchedExistingCount,
      };

      if (matched.resolvedTargets.length === 0) {
        setState({
          kind: "parsed_fatal",
          file,
          message: "仕様書からターゲットを抽出できませんでした。フォーマットを確認してください。",
        });
        return;
      }

      // Service ごとに page_results を分割（左ペインに Service カードが並ぶ）
      const services = matched.resolvedTargets.filter((t) => t.type === "service");
      const nonServices = matched.resolvedTargets.filter((t) => t.type !== "service");

      // 子孫を再帰的に集める helper
      const collectDescendants = (parentId: string): typeof matched.resolvedTargets => {
        const directChildren = nonServices.filter(
          (t) => t.parent_test_target_id === parentId
        );
        const descendants = [...directChildren];
        for (const c of directChildren) {
          descendants.push(...collectDescendants(c.test_target_id ?? c.id));
        }
        return descendants;
      };

      const pageResults = services.length > 0
        ? services.map((svc, i) => ({
            group_index: i,
            path: svc.name,
            test_targets: [svc, ...collectDescendants(svc.test_target_id ?? svc.id)],
            usecases: [],
            is_new_view: !matched.matchedExistingIds.has(svc.test_target_id ?? svc.id),
          }))
        : // Service が無い場合のフォールバック（通常起こらないが防衛）
          [{ group_index: 0, path: "(spec-import)", test_targets: matched.resolvedTargets, usecases: [], is_new_view: false }];

      const response: GenerateFromOperationLogResponse = {
        message: "",
        page_results: pageResults,
      };

      setState({ kind: "parsed_ok", file, summary, response, matchedExistingIds: matched.matchedExistingIds, deletionCandidates: matched.deletionCandidates });
      for (const w of summary.warnings) {
        // eslint-disable-next-line no-console
        console.warn(`[SpecImport] ${w.code}: ${w.message}`, w.context ?? {});
      }
    } catch (err) {
      if (cancelledRef.current) return;
      const message =
        err instanceof SpecError
          ? `${errorToJapanese(err)} (code: ${err.code})`
          : err instanceof Error
            ? err.message
            : String(err);
      setState({ kind: "parsed_fatal", file, message });
    }
  }, [existingTargets]);

  const reset = useCallback(() => setState({ kind: "idle" }), []);

  const handleClose = useCallback(() => {
    cancelledRef.current = true;
    reset();
    onClose();
  }, [onClose, reset]);

  // 警告なしで解析完了したとき、サマリ画面をスキップして自動遷移する
  useEffect(() => {
    if (state.kind !== "parsed_ok") return;
    if (state.summary.warnings.length > 0) return;
    onPreviewReady(state.response, state.summary, state.matchedExistingIds, state.deletionCandidates);
    reset();
    onClose();
  }, [state, onPreviewReady, reset, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Description />
        仕様書のインポート
      </DialogTitle>
      <DialogContent>
        {state.kind !== "parsed_ok" && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            仕様書テンプレート (.xlsx) を選択してください。
          </Typography>
        )}

        {state.kind === "idle" && (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: dragOver ? "action.hover" : "background.paper",
              borderStyle: "dashed",
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const file = e.dataTransfer.files?.[0];
              if (file) handleFile(file);
            }}
            onClick={() => document.getElementById("spec-import-file-input")?.click()}
          >
            <UploadFile fontSize="large" />
            <Typography>ファイルをドラッグ&ドロップ、またはクリックして選択</Typography>
            <input
              id="spec-import-file-input"
              type="file"
              accept=".xlsx"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
              }}
            />
          </Paper>
        )}

        {state.kind === "selected" && (
          <Box>
            <Typography>
              選択中: {state.file.name}（{(state.file.size / (1024 * 1024)).toFixed(2)} MiB）
            </Typography>
          </Box>
        )}

        {state.kind === "parsing" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
            <Typography>解析中...</Typography>
          </Box>
        )}

        {state.kind === "parsed_ok" && state.summary.warnings.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <WarningSection warnings={state.summary.warnings} />
          </Box>
        )}

        {state.kind === "parsed_fatal" && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {state.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Box>
          {state.kind !== "idle" && (
            <Button onClick={reset}>戻る</Button>
          )}
        </Box>
        <Box>
          {state.kind === "idle" ? (
            <Button onClick={handleClose}>キャンセル</Button>
          ) : (
            <>
              <Button onClick={handleClose}>キャンセル</Button>
              {state.kind === "selected" && (
                <Button
                  variant="contained"
                  onClick={() => runParse(state.file)}
                >
                  解析する
                </Button>
              )}
              {state.kind === "parsed_ok" && (
                <Button
                  variant="contained"
                  onClick={() => {
                    onPreviewReady(state.response, state.summary, state.matchedExistingIds, state.deletionCandidates);
                    reset();
                    onClose();
                  }}
                >
                  次へ
                </Button>
              )}
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const WarningSection: React.FC<{ warnings: SpecWarning[] }> = ({ warnings }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mt: 2 }}>
      <Alert
        severity="warning"
        action={
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            data-testid="warning-toggle-button"
          >
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
      >
        警告 {warnings.length} 件
      </Alert>
      <Collapse in={open}>
        <TableContainer component={Paper} sx={{ mt: 1, maxHeight: 240 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>コード</TableCell>
                <TableCell>シート</TableCell>
                <TableCell>行</TableCell>
                <TableCell>メッセージ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {warnings.map((w, i) => (
                <TableRow key={i}>
                  <TableCell>{w.code}</TableCell>
                  <TableCell>{w.location.sheetName}</TableCell>
                  <TableCell>{w.location.rowIndex ?? ""}</TableCell>
                  <TableCell>{warningToJapanese(w)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
};
